import { deepMerge } from '@soitron/global-helpers';
/**
 * @constant STATE
 * @description Defines the possible states of the camera stream lifecycle.
 */
export const STATE = {
    /** @type {0} Camera is stopped, stream is closed, and video element source is cleared. */
    __STOP: 0,
    /** @type {1} Camera stream has been initialized and loaded metadata, but not necessarily playing yet. */
    __START: 1,
    /** @type {2} Video element playback is paused. */
    __PAUSE: 2,
    /** @type {3} Video element is actively playing the stream. */
    __PLAY: 3,
};
/**
 * @class CameraHandler
 * @description Manages the lifecycle, configuration, and state of a camera video stream
 * using the MediaDevices API and an HTMLVideoElement.
 * It provides methods for starting, stopping, pausing, and playing the stream.
 *
 * @example
 * const videoElement = document.querySelector('video');
 * const handler = new CameraHandler(videoElement, {
 *   __cameraVideoConfig: { width: { ideal: 1280 } },
 *   __global: { __videoLoadTime: 5000 }
 * });
 *
 * await handler.__startStream();
 */
export class CameraHandler {
    /**
     * @public
     * @getter
     * @type {string | null}
     * @description Gets the **Device ID** of the currently active camera.
     * @returns {string | null} The active device ID string or null if no stream is active.
     */
    get __getCurrentDeviceId() {
        return this._currentSelectedDeviceId;
    }
    /**
     * @public
     * @getter
     * @type {MediaDeviceInfo[]}
     * @description Gets the list of available video input devices (cameras).
     * @returns {MediaDeviceInfo[]} An array of objects describing the video devices.
     */
    get __getDevicesList() {
        return this._availableVideoDevicesList;
    }
    /**
     * @public
     * @getter
     * @type {MediaStream | null}
     * @description Gets the currently active **MediaStream** object.
     * @returns {MediaStream | null} The active stream object or null.
     */
    get __getCurrentMediaStream() {
        return this._currentMediaStream;
    }
    /**
     * @public
     * @getter
     * @type {MediaStreamTrack | null}
     * @description Gets the **video track** object from the currently active MediaStream.
     * @returns {MediaStreamTrack | null} The active video track or null.
     */
    get __getCurrentTrack() {
        return this._currentTrack;
    }
    /**
     * @public
     * @getter
     * @type {boolean}
     * @description Checks if the application can switch between multiple cameras (i.e., if more than one video device is available).
     * @returns {boolean} True if multiple video devices are found, false otherwise.
     */
    get __canToggleCamera() {
        return this._canSwitchBetweenCameras;
    }
    /**
     * @public
     * @getter
     * @type {TState}
     * @description Gets the **current lifecycle state** of the camera (STOP, START, PAUSE, or PLAY).
     * @returns {TState} The numeric value representing the current camera state.
     */
    get __getCurrentState() {
        return this._currentState;
    }
    /**
     * @public
     * @getter
     * @type {HTMLVideoElement}
     * @description Gets the reference to the underlying **HTMLVideoElement** used for displaying the stream.
     * @returns {HTMLVideoElement} The video element instance.
     */
    get __getVideoElement() {
        return this._videoElement;
    }
    /**
     * @public
     * @getter
     * @type {MediaTrackConstraints}
     * @description Gets the configuration object used to request the camera stream via `getUserMedia`.
     * It holds the desired facing mode, resolution, and potentially device ID.
     * @returns {MediaTrackConstraints} The current video track constraints.
     * @example
     * handler.__cameraVideoConfig.width = { ideal: 1280 };
     */
    get __cameraVideoConfig() {
        return this._config.__cameraVideoConfig;
    }
    /**
     * @public
     * @setter
     * @param {MediaTrackConstraints} cameraVideoConfig - The new constraints to be set.
     * @description Sets the configuration object used for stream requests.
     */
    set __cameraVideoConfig(cameraVideoConfig) {
        this._config.__cameraVideoConfig = cameraVideoConfig;
    }
    /**
     * @public
     * @getter
     * @type {IConfigGlobal}
     * @description Gets the global configuration settings.
     * @returns {IConfigGlobal} The global configuration object.
     */
    get __cameraGlobalConfig() {
        return this._config.__global;
    }
    /**
     * @private
     * @type {string | null}
     * @description Stores the **Device ID** of the currently selected camera track.
     * Uses the required single underscore prefix for private properties.
     */
    _currentSelectedDeviceId = null;
    /**
     * @private
     * @type {boolean}
     * @description Flag indicating if the video stream has successfully loaded its metadata.
     */
    _isStreamReady = false;
    /**
     * @private
     * @type {boolean}
     * @description Flag to prevent concurrent stream start operations (race condition protection).
     */
    _isStarting = false;
    /**
     * @private
     * @type {MediaDeviceInfo[]}
     * @description List of discovered **video input devices**.
     */
    _availableVideoDevicesList = [];
    /**
     * @private
     * @type {TState}
     * @description The **current lifecycle state** of the camera stream (e.g., STOP, PLAY).
     */
    _currentState = STATE.__STOP;
    /**
     * @private
     * @type {boolean}
     * @description Indicates whether switching between multiple cameras is technically possible.
     */
    _canSwitchBetweenCameras = false;
    /**
     * @private
     * @type {MediaStreamTrack | null}
     * @description The actual **video track object** from the MediaStream.
     */
    _currentTrack = null;
    /**
     * @private
     * @type {MediaStream | null}
     * @description The main **stream object** received from `getUserMedia`.
     */
    _currentMediaStream = null;
    /**
     * @private
     * @type {AbortController | null}
     * @description Used to **cancel the 'loadedmetadata' event listener** if the stream is stopped before loading is complete.
     */
    _metadataLoadingAbortController = null;
    _onReadyCallback = null;
    /**
     * @private
     * @readonly
     * @type {Promise<void>}
     * @description A Promise that resolves after checking device capabilities (device list, toggling possibility).
     */
    _deviceCapabilitiesCheckPromise;
    /**
     * @private
     * @readonly
     * @type {HTMLVideoElement}
     * @description The **HTMLVideoElement** where the stream will be displayed.
     */
    _videoElement;
    /**
     * @private
     * @readonly
     * @description The internal, merged configuration object including default constraints.
     */
    _config = {
        __cameraVideoConfig: {
            // Initially requests the back camera ('environment').
            facingMode: { exact: 'environment' },
            // Requests an ideal width resolution of 1920 pixels.
            width: { ideal: 1920 },
            // Requests an ideal height resolution of 1080 pixels.
            height: { ideal: 1080 },
        },
        __global: {
            // Default timeout of 10 seconds for metadata loading.
            __videoLoadTime: 10000,
            __autoplay: false,
        },
        // Image optimization config: false = vypnuté, undefined = auto defaults, objekt = vlastné nastavenia
        __imageOptimization: undefined,
    };
    /**
     * @constructor
     * @description Initializes the CameraHandler, validates the video element,
     * merges configuration, and starts checking device capabilities.
     * @param {HTMLVideoElement | null} videoElement - The video element to display the stream.
     * @param {IConfig} [config] - Optional custom configuration constraints.
     * @example
     * const handler = new CameraHandler(document.querySelector('video'), {
     *   __global: { __dataStream: true }
     * });
     */
    constructor(videoElement, config) {
        // Use provided video element or create a new one if null.
        this._videoElement = videoElement || document.createElement('video');
        this._videoElement.setAttribute('autoplay', '');
        this._videoElement.setAttribute('muted', '');
        // Merge custom video configuration with defaults.
        this.__cameraVideoConfig = deepMerge(this.__cameraVideoConfig, config?.__cameraVideoConfig || {});
        // Merge custom global configuration (e.g., timeout) with defaults.
        this._config.__global = deepMerge(this.__cameraGlobalConfig, config?.__global || {});
        // Validate and enforce minimum videoLoadTime of 5 seconds to prevent premature timeouts.
        if (this._config.__global.__videoLoadTime !== undefined) {
            this._config.__global.__videoLoadTime = Math.max(this.__cameraGlobalConfig.__videoLoadTime || 1, 5000);
        }
        // Image optimization config
        if (config?.__imageOptimization !== undefined) {
            this._config.__imageOptimization = config.__imageOptimization;
        }
        // Start the asynchronous check for device capabilities immediately upon instantiation.
        // This allows for lazy initialization and prevents blocking the constructor.
        this._deviceCapabilitiesCheckPromise = this._checkDeviceCapabilities();
    }
    __onReady(callback) {
        if (this._onReadyCallback || !callback) {
            return;
        }
        this._onReadyCallback = callback;
    }
    /**
     * @public
     * @async
     * @method
     * @description Starts the camera stream acquisition process: checks capabilities,
     * requests the stream, attaches it to the video element, and waits for metadata.
     * @returns {Promise<void>} A Promise that resolves when the stream is ready or when the start operation is skipped.
     * @example
     * await handler.__startStream();
     */
    async __startStream(autoplay) {
        // Guard against concurrent starts to prevent race conditions.
        if (this._isStarting) {
            console.warn('Stream start already in progress');
            return;
        }
        // Wait for the asynchronous device capability check to complete before starting.
        // This ensures we know what cameras are available before attempting to start.
        await this._deviceCapabilitiesCheckPromise;
        // If already active or starting, return immediately to prevent redundant operations.
        if (this._currentState !== STATE.__STOP) {
            return;
        }
        // Set flag to indicate stream start is in progress.
        this._isStarting = true;
        try {
            // Safely extract the facingMode constraint from the current configuration.
            const facingMode = this.__cameraVideoConfig?.facingMode;
            // If camera toggling is NOT possible (only one camera) AND an 'exact' facingMode is set,
            // force the facingMode to be the 'exact' value, ensuring the constraint is correctly applied.
            if (!this._canSwitchBetweenCameras && facingMode?.exact) {
                this.__cameraVideoConfig.facingMode = facingMode.exact;
            }
            // Attempt to get the media stream from the device, exiting if the attempt fails.
            if (!(await this._acquireMediaStream())) {
                return;
            }
            // Create a Promise that resolves when the video element loads the stream metadata.
            const loadedMetadataPromise = new Promise((resolve, reject) => {
                // If metadata is already loaded (readyState > 0), resolve immediately.
                if (this._videoElement.readyState > 0) {
                    return resolve(true);
                }
                // Helper function to clear the timeout and prevent memory leaks.
                const clear = () => {
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                };
                // Create a new AbortController to manage the timer, 'loadedmetadata' and 'error' listener.
                this._metadataLoadingAbortController = new AbortController();
                const signal = this._metadataLoadingAbortController.signal;
                // Set a timeout for loading the stream metadata to prevent indefinite waiting.
                // noinspection TypeScriptUnresolvedReference
                let timeoutId = setTimeout(() => {
                    clear();
                    this._clearStreamResources();
                    reject(new Error(`Loading video stream metadata timed out after ${this.__cameraGlobalConfig.__videoLoadTime}ms. Camera stream failed to start.`));
                }, this.__cameraGlobalConfig.__videoLoadTime);
                // Listener for manual abort (e.g., stopping the stream while loading).
                signal.addEventListener('abort', () => {
                    clear();
                    this._clearStreamResources();
                    reject(new Error(`Camera stream startup was manually aborted.`));
                }, { once: true });
                // Listener for successful metadata loading.
                this._videoElement.addEventListener('loadedmetadata', () => {
                    clear();
                    console.info('Metadata loaded successfully.');
                    resolve(true);
                }, { signal, once: true });
                // Listener for video element error during loading.
                this._videoElement.addEventListener('error', () => {
                    clear();
                    this._clearStreamResources();
                    reject(new Error(`Video element encountered an error while loading the stream. Camera stream failed to start.`));
                }, { signal, once: true });
            }).catch((e) => {
                // Emit error event if Promise is rejected.
                return false;
            });
            // Assign the acquired MediaStream to the video element's source object.
            this._videoElement.srcObject = this._currentMediaStream;
            // Wait for the loaded metadata Promise to resolve, setting the stream readiness flag.
            if ((this._isStreamReady = await loadedMetadataPromise)) {
                // If metadata loaded successfully, clear the abort controller as it is no longer needed.
                this._metadataLoadingAbortController = null;
                // Update state to indicate stream is started and ready.
                this._currentState = STATE.__START;
                this._onReadyCallback?.();
                if (this.__cameraGlobalConfig.__autoplay || autoplay) {
                    // Begin playing the newly acquired stream.
                    await this.__playVideoPlayback();
                }
            }
            else {
                // If metadata failed to load (e.g., stream was aborted), perform the necessary cleanup.
                this._clearStreamResources();
            }
        }
        finally {
            // Always reset the starting flag, even if an error occurs.
            this._isStarting = false;
        }
    }
    /**
     * @public
     * @async
     * @method
     * @description Stops the camera stream by clearing all resources and returning the state to STOP.
     * @returns {Promise<void>} A Promise that resolves when the stream is stopped.
     * @example
     * await handler.__stopStream();
     */
    async __stopStream() {
        // Check if the camera is already in the STOP state to avoid redundant operations.
        if (this._currentState === STATE.__STOP) {
            return;
        }
        // Call the internal cleanup method to stop tracks, clear properties, and reset state.
        this._clearStreamResources();
    }
    /**
     * @public
     * @async
     * @method
     * @description Pauses the video playback if the stream is currently playing and ready.
     * @returns {Promise<void>} A Promise that resolves when the video is paused.
     * @example
     * await handler.__pauseVideoPlayback();
     */
    async __pauseVideoPlayback() {
        // Check if the current state is not PLAY or if the stream is not yet ready.
        if (this._currentState !== STATE.__PLAY || !this._isStreamReady) {
            return;
        }
        // Pause the video element playback.
        this._videoElement.pause();
        // Update state to PAUSE.
        this._currentState = STATE.__PAUSE;
    }
    /**
     * @public
     * @async
     * @method
     * @description Starts or resumes video playback if the stream is in START (loaded) or PAUSE state.
     * @returns {Promise<void>} A Promise that resolves when the video playback starts.
     * @example
     * await handler.__playVideoPlayback();
     */
    async __playVideoPlayback() {
        // Only allow playback if in START or PAUSE state.
        if (this._currentState !== STATE.__START && this._currentState !== STATE.__PAUSE) {
            return;
        }
        try {
            // Attempt to play the video (returns a Promise that may reject).
            await this._videoElement.play();
            // Update state to PLAY on success.
            this._currentState = STATE.__PLAY;
        }
        catch (e) {
            // If we were in START state and play failed, transition to PAUSE.
            // This maintains state consistency - we have a loaded stream but it's not playing.
            if (this._currentState === STATE.__START) {
                this._currentState = STATE.__PAUSE;
            }
        }
    }
    /**
     * @public
     * @async
     * @method
     * @description Stops the current stream, starts a new one, and immediately begins playback.
     * Used for reapplying constraints or switching cameras.
     * @returns {Promise<void>} A Promise that resolves after the restart process is complete.
     * @example
     * await handler.__restartStreamAndPlay();
     */
    async __restartStreamAndPlay() {
        // Stop the current stream to release resources.
        await this.__stopStream();
        // Start a new stream acquisition.
        await this.__startStream();
    }
    /**
     * @public
     * @method
     * @description Applies a **horizontal flip** (scaleX(-1)) CSS transform to the video element
     * primarily when facingMode is 'user' (front camera) to correct mirror effect, or
     * removes it otherwise, if camera switching is possible.
     * @param {ConstrainDOMString} [mode=''] - Optional explicit facing mode to check against (defaults to an empty string).
     * @returns {void}
     * @example
     * // Flips the video element for 'user' mode
     * handler.__applyHorizontalFlip('user');
     */
    __applyHorizontalFlip(mode = '') {
        // Check if the explicit mode is 'user' OR the current config facingMode is 'user'
        // OR camera switching is not possible (in which case we always flip for consistency).
        if ([mode, this.__cameraVideoConfig.facingMode].includes('user') || !this._canSwitchBetweenCameras) {
            // Set a data attribute that a CSS selector can use to apply the flip transform.
            // CSS rule example: video[data-facing-mode="user"] { transform: scaleX(-1); }
            this.__getVideoElement.dataset.facingMode = 'user';
        }
        else {
            // Remove the data attribute to remove the flip (normal orientation).
            delete this.__getVideoElement.dataset.facingMode;
        }
    }
    /**
     * @private
     * @async
     * @method
     * @description Requests a new media stream from the device based on the current configuration.
     * Updates internal state with the new stream, track, and device ID.
     * @returns {Promise<boolean>} A Promise that resolves to true if the stream was successfully acquired and track settings updated, false otherwise.
     */
    async _acquireMediaStream() {
        try {
            // Apply or remove the horizontal flip transformation before stream acquisition.
            this.__applyHorizontalFlip();
            // Request the media stream using the current configuration.
            this._currentMediaStream = await window.navigator.mediaDevices.getUserMedia({
                // Ensure audio is disabled (we only want video).
                audio: false,
                // Use the video constraints defined in the public configuration property.
                video: this.__cameraVideoConfig,
            });
            // Get the first video track from the acquired stream.
            this._currentTrack = this._currentMediaStream.getVideoTracks()[0];
            // Get the actual settings that the browser applied to the track.
            // These may differ from requested constraints.
            const trackSettings = this._currentTrack.getSettings();
            // Check if the acquired track has a device ID in its settings.
            if (trackSettings.deviceId) {
                // Store the successful device ID for future reference.
                this._currentSelectedDeviceId = trackSettings.deviceId;
                // Remove the general 'facingMode' constraint from the configuration.
                delete this.__cameraVideoConfig.facingMode;
                // Replace 'facingMode' with the 'deviceId' constraint for future restarts/swaps,
                // ensuring the exact same device is used next time (more reliable than facingMode).
                this.__cameraVideoConfig = deepMerge(this.__cameraVideoConfig, {
                    deviceId: { exact: trackSettings.deviceId },
                });
            }
            // Optimalizácia parametrov kamery (expozícia, WB, focus)
            await this._applyImageOptimizations();
            // Return true indicating successful stream acquisition.
            return true;
        }
        catch (e) {
            // If a specific device ID was requested but failed, remove the deviceId constraint
            // for a fallback attempt (will fall back to facingMode or default camera).
            if (this.__cameraVideoConfig.deviceId) {
                this.__cameraVideoConfig.deviceId = undefined;
            }
            // Return false indicating stream acquisition failed.
            return false;
        }
    }
    /**
     * @private
     * @async
     * @method
     * @description Checks the browser/device capabilities for media access.
     * Specifically, it enumerates video devices to determine if camera toggling is possible.
     * @returns {Promise<void>} A Promise that resolves after the capability check is complete.
     */
    async _checkDeviceCapabilities() {
        try {
            // Asynchronously enumerate all available media devices.
            this._availableVideoDevicesList = (await navigator.mediaDevices.enumerateDevices())
                // Filter the list to include only video input devices ('videoinput').
                .filter(device => device.kind === 'videoinput');
            // Determine if camera toggling is possible (more than one video device found).
            this._canSwitchBetweenCameras = this._availableVideoDevicesList.length > 1;
        }
        catch (e) {
            // If enumeration fails (e.g., due to permissions not granted yet),
            // assume toggling is not possible and emit error.
            this._canSwitchBetweenCameras = false;
        }
    }
    /**
     * @description Vráti capabilities aktuálneho video tracku.
     *   Použiteľné na diagnostiku - zistenie, ktoré parametre kamera reálne podporuje.
     *   Chrome na Windows používa IAMCameraControl - výsledok sa líši medzi platformami.
     */
    get __getTrackCapabilities() {
        if (!this._currentTrack)
            return null;
        try {
            return this._currentTrack.getCapabilities();
        }
        catch {
            return null;
        }
    }
    /**
     * @description Vráti aktuálne nastavenia video tracku (reálne hodnoty, nie constrainty).
     */
    get __getTrackSettings() {
        if (!this._currentTrack)
            return null;
        try {
            return this._currentTrack.getSettings();
        }
        catch {
            return null;
        }
    }
    /**
     * @description Aplikuje image constraints na aktívny track.
     *   Prepnutie režimu (napr. exposureMode: 'manual') a nastavenie hodnoty
     *   (napr. exposureTime) sa robia v dvoch separátnych volaniach applyConstraints.
     *   Prehliadač ignoruje hodnotu ak sa pošle spolu s prepnutím režimu.
     */
    async __applyImageConstraints(constraints) {
        if (!this._currentTrack)
            return false;
        const caps = this.__getTrackCapabilities;
        if (!caps)
            return false;
        let applied = false;
        // Fáza 1: prepnutie režimov (mode switches)
        const modes = {};
        const modeProps = [
            { key: 'exposureMode', capKey: 'exposureMode' },
            { key: 'whiteBalanceMode', capKey: 'whiteBalanceMode' },
            { key: 'focusMode', capKey: 'focusMode' },
        ];
        for (const { key, capKey } of modeProps) {
            const value = constraints[key];
            const supported = caps[capKey];
            if (value && supported?.includes(value)) {
                modes[capKey] = value;
            }
        }
        if (Object.keys(modes).length > 0) {
            try {
                await this._currentTrack.applyConstraints({ advanced: [modes] });
                applied = true;
                await new Promise(r => setTimeout(r, 200));
            }
            catch (e) {
                console.warn('[CameraHandler] Mode switch failed:', e.message);
            }
        }
        // Fáza 2: nastavenie hodnôt (po prepnutí režimov)
        const values = {};
        const numericProps = [
            'exposureTime', 'exposureCompensation', 'colorTemperature',
            'focusDistance', 'brightness', 'contrast', 'sharpness', 'iso',
        ];
        for (const prop of numericProps) {
            const value = constraints[prop];
            const range = caps[prop];
            if (value !== undefined && range) {
                values[prop] = Math.max(range.min, Math.min(range.max, value));
            }
        }
        if (Object.keys(values).length > 0) {
            try {
                await this._currentTrack.applyConstraints({ advanced: [values] });
                applied = true;
            }
            catch (e) {
                console.warn('[CameraHandler] Value constraints failed:', e.message);
            }
        }
        return applied;
    }
    /**
     * @description Automatická optimalizácia parametrov kamery po získaní tracku.
     *   Aplikuje defaults per-camera mergnuté s používateľským configom.
     */
    async _applyImageOptimizations() {
        if (!this._currentTrack)
            return;
        const caps = this.__getTrackCapabilities;
        if (!caps)
            return;
        const imageConfig = this._config.__imageOptimization;
        if (imageConfig === false)
            return;
        const settings = this._currentTrack.getSettings();
        const isFront = settings.facingMode === 'user';
        console.info(`[CameraHandler] ${isFront ? 'FRONT' : 'BACK'} camera settings:`, JSON.stringify(settings, null, 2));
        // Defaults per-camera
        // IAMCameraControl na Windows: exposureCompensation 0-128 (0=najtmavšie, 64=stred, 128=najsvetlejšie)
        const defaults = isFront
            ? { exposureCompensation: 45, contrast: 50, sharpness: 55 }
            : { exposureCompensation: 30, contrast: 40, sharpness: 55 };
        // Merge: defaults <- user config (user config prepisuje defaults)
        let merged = { ...defaults };
        if (imageConfig && typeof imageConfig === 'object') {
            const perCamera = isFront ? imageConfig.front : imageConfig.back;
            if (perCamera) {
                merged = { ...defaults, ...perCamera };
            }
            else if (!imageConfig.front && !imageConfig.back) {
                merged = { ...defaults, ...imageConfig };
            }
        }
        // Odstrániť properties ktoré kamera nepodporuje
        for (const key of Object.keys(merged)) {
            if (!caps[key])
                delete merged[key];
        }
        if (Object.keys(merged).length > 0) {
            console.info(`[CameraHandler] ${isFront ? 'FRONT' : 'BACK'} applying:`, JSON.stringify(merged));
            await this.__applyImageConstraints(merged);
        }
    }
    /**
     * @private
     * @method
     * @description Internal method to stop all stream tracks, release resources, and reset state variables.
     * Clears video element source, stops tracks, and resets all internal properties to initial values.
     * @returns {void}
     */
    _clearStreamResources() {
        // If the abort controller exists, signal to cancel any pending 'loadedmetadata' listeners.
        this._metadataLoadingAbortController?.abort();
        // Clear the reference to the abort controller.
        this._metadataLoadingAbortController = null;
        // Check if a video track is currently active.
        if (this._currentTrack) {
            // Stop the track, releasing the camera hardware.
            this._currentTrack.stop();
        }
        // Set the state back to STOP.
        this._currentState = STATE.__STOP;
        // Reset the readiness flag.
        this._isStreamReady = false;
        // Reset all stream-related private properties to their initial 'null' or 'default' states.
        this._currentTrack = null;
        this._currentMediaStream = null;
        this._currentSelectedDeviceId = null;
        // Clear the video element's source to stop playback and display.
        this._videoElement.srcObject = null;
    }
}
//# sourceMappingURL=camera-handler.js.map